import { RootStore } from '~/stores/root.store';
import { ToastType } from '@honeybook/hbui/dist/components/HBToast/HBToast.types';
import { ISessionConfig } from '~/types/server.types';
import { action } from 'mobx';
import { Flow } from '~/models/flow.model';
import { TFullTimezone } from '~/utils/timezone.utils';
import { Dayjs } from 'dayjs';
import { PartialWithRequired } from '~/types/app.types';
export class ScheduleController {
    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
    }

    // This function return the session configs of specific user if it dosen't exist in rootStore
    async fetchScheduleSessionConfigs(
        userId?: string,
        forUserId?: string,
        ignoreDayAvailabilities: boolean = true,
        ignoreTimeFrame: boolean = false,
        fromDate?: Dayjs
    ) {
        const {
            scheduleService,
            userStore: { currUser }
        } = this.rootStore;

        const { session_configs } =
            await scheduleService.getScheduleSessionConfigs({
                userId: userId ? userId : currUser._id,
                forUserId,
                params: {
                    ignore_day_availabilities: ignoreDayAvailabilities,
                    from_date: fromDate?.format('YYYY-MM-DD'),
                    ignore_time_frame: ignoreTimeFrame
                }
            });

        this.updateScheduleSessionConfigs(session_configs);
        return session_configs;
    }

    async fetchCompanyScheduleSessionConfigs() {
        const {
            scheduleService,
            companyStore: { currCompany }
        } = this.rootStore;

        const { session_configs } =
            await scheduleService.getCompanyScheduleSessionConfigs({
                companyId: currCompany._id
            });

        this.updateScheduleSessionConfigs(session_configs);
        return session_configs;
    }

    async getAvailabilities(params: {
        debugMode?: boolean;
        startDate: string;
        endDate: string;
        timezone: TFullTimezone;
        sessionTypeId: string;
        placementId: string;
        workspaceId: string;
        source: string;
    }) {
        const { scheduleService } = this.rootStore;
        return scheduleService.getAvailabilities(params);
    }

    @action.bound
    updateScheduleSessionConfigs(session_configs: ISessionConfig[]) {
        const {
            builderUiStore: { selectedFlow },
            companyStore: { currCompany }
        } = this.rootStore;

        const sortedSessionConfigList = session_configs
            ?.slice()
            .sort((a, b) => Number(b.enabled) - Number(a.enabled));

        currCompany.setScheduleSessionConfigs(sortedSessionConfigList);

        if (!selectedFlow?.scheduleContainerBlock) {
            return;
        }
        const relevantSessionConfig = sortedSessionConfigList?.find(
            curr =>
                curr._id ===
                selectedFlow.scheduleContainerBlock.session_config_id
        );

        selectedFlow.scheduleContainerBlock.setSessionConfig(
            relevantSessionConfig
        );

        selectedFlow.sessionSelectionBlock?.allServiceItems.forEach(
            serviceItem => {
                const currSessionConfig = session_configs.find(
                    sessionConfig =>
                        sessionConfig._id === serviceItem.session_config_id
                );
                serviceItem.session_config = currSessionConfig;
            }
        );
    }

    async cancelSession({
        flow,
        variableId
    }: {
        flow: Flow;
        variableId: string;
    }) {
        const { scheduleService, flowCtrl } = this.rootStore;
        try {
            const response = await scheduleService.cancelSession({
                flowId: flow._id,
                variableId
            });
            flowCtrl.syncFlowRevisionsAfterActionOnActiveRevision(
                response,
                flow
            );
            flow.setEligibleForMarkAsCompleted(
                response.is_eligible_for_mark_as_completed
            );
            flow.setStatus(response.status);
        } catch (error) {
            this.rootStore.toastStore.add({
                text: error.data.error_message,
                type: ToastType.Error
            });
        }
    }

    createScheduleSessionConfig(data: ISessionConfig) {
        const {
            scheduleService,
            userStore: { currUser }
        } = this.rootStore;
        return scheduleService.createScheduleSessionConfig({
            data,
            userId: currUser._id
        });
    }

    async updateSession(
        data: PartialWithRequired<ISessionConfig, '_id'>,
        userId?: string
    ) {
        const {
            scheduleService,
            userStore: { currUser }
        } = this.rootStore;

        const updatedSessionConfig = await scheduleService.updateSession({
            userId: userId || currUser._id,
            data
        });

        return updatedSessionConfig;
    }
}
